import { useState, useEffect, Fragment } from "react";
import {
  fetchApplicationInsights,
  fetchInsightsCounts,
  getOrgUnitTree,
  type ApplicationInsightsData,
  type InsightsCountsResponse,
  type InsightSection,
  type InsightItem,
  type InsightDetail,
  type TopPoamItem,
  type OrgUnit,
  type InsightsFilter,
} from "../../api/frameworksApi";
import { MultiSelectDropdown } from "../components/MultiSelectDropdown";
import {
  TrendingUp,
  AlertTriangle,
  Minus,
  ChevronRight,
  ChevronDown,
  ArrowRight,
  CheckCircle2,
  Activity,
  X,
  RefreshCw,
  Loader2,
  ShieldAlert,
  Users,
  ListChecks,
  Clock,
  LayoutDashboard,
  LayoutGrid,
  AppWindow,
  Paperclip,
  ClipboardCheck,
  FileCheck2,
  FileText,
  CalendarClock,
  Cloud,
  Server,
} from "lucide-react";
import { useToastStore } from "../../stores/toastStore";
import { DataTable, type Column } from "../components/DataTable";
import { DashboardRouter } from "./DashboardRouter";
import { QuadrantDashboard, QuadrantControlsSlotContext } from "./QuadrantDashboard";
import { DashboardControlsSlotContext } from "./GRCManagerDashboard/GRCManagerDashboard";
import { useAuthStore } from "../../stores/authStore";
import { roleDashboardTitle, roleDashboardSubtitle } from "./GRCDashboardNew/grcWidgets";
import {
  KeyMetricsConfigModal,
  type ConfigCard,
  type ConfigGroup,
  type KeyMetricsConfig,
} from "../components/KeyMetricsConfigModal";
import { Settings2 } from "lucide-react";
import { Tip } from "../components/ui/tooltip";
import { useUiContextStore } from "../../stores/uiContextStore";

/* ══════════════════════════════════════════════════════════════════════
   EXECUTIVE INSIGHTS — Application "what changed" summary.
   100% driven by GET /dashboard/insights/applications (no mock data).
   Three cards — What Needs Attention / What Improved / What Remained Stable — each
   lists its insight items; clicking an item (or "View all") drills into a
   side drawer with the per-application/finding detail rows.
   ══════════════════════════════════════════════════════════════════════ */

/* Empty scaffold shown until the live endpoint responds (not mock data). */
const EMPTY_INSIGHTS: ApplicationInsightsData = {
  worse:    { type: "worse",    label: "What Needs Attention",     count: 0, items: [] },
  improved: { type: "improved", label: "What Improved",      count: 0, items: [] },
  stable:   { type: "stable",   label: "What Remained Stable", count: 0, items: [] },
};

/* Hardcoded section headers — used instead of whatever label the API returns
   for the three primary cards, so the UI always shows our wording. The
   worsePoams/inactiveUsers sections keep their live API labels. */
const LOCAL_SECTION_LABELS: Partial<Record<InsightSection["type"], string>> = {
  worse: "What Needs Attention",
  improved: "What Improved",
  stable: "What Remained Stable",
};

/* Returns a copy of the insights data with our hardcoded section labels applied. */
const withLocalLabels = (data: ApplicationInsightsData): ApplicationInsightsData => {
  const next = { ...data };
  (Object.keys(LOCAL_SECTION_LABELS) as InsightSection["type"][]).forEach((key) => {
    const section = next[key];
    const label = LOCAL_SECTION_LABELS[key];
    if (section && label) next[key] = { ...section, label };
  });
  return next;
};

/* The API may return section keys/types in lowercase (e.g. "worsepoams" instead
   of "worsePoams"). Normalize them so the rest of the page can rely on the
   camelCase keys and the typed `type` discriminants. */
const normalizeInsights = (data: ApplicationInsightsData): ApplicationInsightsData => {
  const raw = data as unknown as Record<string, InsightSection | undefined>;
  const next = { ...data };
  const fix = (
    camelKey: "worsePoams" | "inactiveUsers",
    type: InsightSection["type"],
    aliases: string[],
  ) => {
    const found = next[camelKey] ?? aliases.map((a) => raw[a]).find(Boolean);
    if (found) next[camelKey] = { ...found, type };
  };
  fix("worsePoams", "worsePoams", ["worsepoams", "worse_poams"]);
  fix("inactiveUsers", "inactiveUsers", ["inactiveusers", "inactive_users"]);
  return next;
};

/* Snake_case / lowercase status -> "Title Case" for display. */
const humanizeStatus = (s?: string) =>
  s ? s.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) : "-";

/* Severity word -> badge color classes. */
const SEVERITY_BADGE: Record<string, string> = {
  critical: "bg-red-100 text-red-700 dark:bg-red-500/15 dark:text-red-400",
  high: "bg-orange-100 text-orange-700 dark:bg-orange-500/15 dark:text-orange-400",
  medium: "bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-400",
  low: "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-400",
};

/* ── Top POAM items -> Management Actions row mapping ─────────────────────
   Priority  <- severity
   Action    <- remediationPlanTitle (falls back to "Remediation plan pending")
   Owner     <- assignees
   Target    <- scheduledCompletionDate
   Progress  <- progressPercent                                              */

type ActionRow = {
  priority: string;
  badge: string;
  label: string;
  owner: string;
  initials: string;
  date: string;
  progress: number;
  color: string;
  sspName?: string;
  applicationName?: string;
};

/* Badge styling + progress-bar colour keyed on severity. */
function severityStyle(severity?: string): { badge: string; color: string } {
  switch ((severity || "").trim().toLowerCase()) {
    case "critical":
      return {
        badge:
          "border border-red-100 bg-red-50 text-red-600 dark:border-red-500/20 dark:bg-red-500/10 dark:text-red-400",
        color: "#ef4444",
      };
    case "high":
      return {
        badge:
          "border border-orange-100 bg-orange-50 text-orange-600 dark:border-orange-500/20 dark:bg-orange-500/10 dark:text-orange-400",
        color: "#f97316",
      };
    case "medium":
      return {
        badge:
          "border border-amber-100 bg-amber-50 text-amber-600 dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-400",
        color: "#f59e0b",
      };
    case "low":
      return {
        badge:
          "border border-emerald-100 bg-emerald-50 text-emerald-600 dark:border-emerald-500/20 dark:bg-emerald-500/10 dark:text-emerald-400",
        color: "#10b981",
      };
    default:
      return {
        badge:
          "border border-slate-200 bg-slate-50 text-slate-600 dark:border-white/[0.08] dark:bg-white/[0.04] dark:text-white/60",
        color: "#94a3b8",
      };
  }
}

/* Resolve an assignee (string or object) to a display name. */
function assigneeName(a: string | { name?: string; displayName?: string }): string {
  if (typeof a === "string") return a;
  return a?.displayName || a?.name || "";
}

/* Two-letter initials from a name; "--" when unknown. */
function toInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "--";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/* Format an ISO date as e.g. "Jun 22, 2026"; "Not set" when missing. */
function formatTargetDate(iso?: string): string {
  if (!iso) return "Not set";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "Not set";
  return d.toLocaleDateString("en-US", { month: "short", day: "2-digit", year: "numeric" });
}

/* Inline table caps at this many rows; the rest live in the slide-in panel. */
const MAX_VISIBLE_ACTIONS = 5;

function toActionRow(item: TopPoamItem): ActionRow {
  const { badge, color } = severityStyle(item.severity);
  const priority = item.severity
    ? item.severity.charAt(0).toUpperCase() + item.severity.slice(1).toLowerCase()
    : "Unknown";
  const names = (item.assignees ?? []).map(assigneeName).filter(Boolean);
  const owner = names.length ? names.join(", ") : "Unassigned";
  return {
    priority,
    badge,
    label: item.remediationPlanTitle?.trim() || "Remediation plan pending",
    owner,
    initials: names.length ? toInitials(names[0]) : "--",
    date: formatTargetDate(item.scheduledCompletionDate),
    progress: item.progressPercent ?? 0,
    color,
    sspName: item.sspName?.trim() || undefined,
    applicationName: item.sspSourceName?.trim() || item.applicationName?.trim() || undefined,
  };
}

/* The headline number for a section card — the first item's metric (e.g. "63"),
   falling back to the total number of detail rows, then "0". */
function sectionHeadlineMetric(s?: InsightSection): string {
  if (!s || !s.items?.length) return "0";
  const m = s.items[0].metric?.trim();
  if (m) return m;
  const total = s.items.reduce((n, it) => n + (it.details?.length || 0), 0);
  return String(total);
}

/* Section key -> drawer style config key. */
const SECTION_KEY = {
  worse: "issues",
  improved: "improvements",
  stable: "stable",
  worsePoams: "regressed",
  inactiveUsers: "inactive",
} as const satisfies Record<InsightSection["type"], string>;

/* Metric values that are really just statuses / priority words — not shown in
   the detail rows (they duplicate the item's own priority/status). */
const HIDDEN_METRICS = new Set([
  "open", "in_progress", "in progress", "in-progress", "closed", "resolved",
  "low", "medium", "high", "critical",
]);
const isHiddenMetric = (m?: string) => !m || HIDDEN_METRICS.has(m.trim().toLowerCase());

/* The three sections that render as cards on the page (worse/improved/stable).
   worsePoams/inactiveUsers are InsightSections too, but surface as metric
   cards + drill-down slides, so they don't need this card theming. */
type CardSectionType = "worse" | "improved" | "stable";

/* Per-section theming for the card header + item rows. */
const SECTION_THEME: Record<
  CardSectionType,
  {
    icon: typeof AlertTriangle;
    text: string;          // strong accent text/number
    textMuted: string;     // softer accent text (footer link)
    iconBox: string;       // small header icon chip
    countBadge: string;    // header count pill
    headerBg: string;      // header gradient
    headerBorder: string;
    rowHover: string;      // item-card hover background
    emptyText: string;     // empty-state message
    viewAll: string;       // "view all" label
  }
> = {
  worse: {
    icon: AlertTriangle,
    text: "text-red-600 dark:text-red-400",
    textMuted: "text-red-500 dark:text-red-400/80",
    iconBox: "bg-red-100 dark:bg-red-500/15 text-red-600 dark:text-red-400",
    countBadge: "text-red-600 dark:text-red-400 bg-red-100 dark:bg-red-500/15",
    headerBg: "bg-white dark:bg-transparent",
    headerBorder: "border-red-100 dark:border-red-500/[0.08]",
    rowHover: "hover:border-red-300 dark:hover:border-red-500/40",
    emptyText: "Nothing got worse this period.",
    viewAll: "View all issues",
  },
  improved: {
    icon: TrendingUp,
    text: "text-emerald-600 dark:text-emerald-400",
    textMuted: "text-emerald-600 dark:text-emerald-400/80",
    iconBox: "bg-emerald-100 dark:bg-emerald-500/15 text-emerald-600 dark:text-emerald-400",
    countBadge: "text-emerald-600 dark:text-emerald-400 bg-emerald-100 dark:bg-emerald-500/15",
    headerBg: "bg-white dark:bg-transparent",
    headerBorder: "border-emerald-100 dark:border-emerald-500/[0.08]",
    rowHover: "hover:border-emerald-300 dark:hover:border-emerald-500/40",
    emptyText: "No improvements this period.",
    viewAll: "View all improvements",
  },
  stable: {
    icon: Minus,
    text: "text-amber-600 dark:text-amber-400",
    textMuted: "text-amber-600 dark:text-amber-400/80",
    iconBox: "bg-amber-100 dark:bg-amber-500/15 text-amber-600 dark:text-amber-400",
    countBadge: "text-amber-600 dark:text-amber-400 bg-amber-100 dark:bg-amber-500/15",
    headerBg: "bg-white dark:bg-transparent",
    headerBorder: "border-amber-100 dark:border-amber-500/[0.08]",
    rowHover: "hover:border-amber-300 dark:hover:border-amber-500/40",
    emptyText: "No stable areas this period.",
    viewAll: "View all stable areas",
  },
};

/* Drawer (drill-down) per-section styling. */
const DRAWER_CONFIG = {
  issues: {
    title: "All Issues",
    subtitle: "Critical areas requiring immediate management focus",
    icon: AlertTriangle,
    iconBg: "bg-red-100 dark:bg-red-500/15",
    iconColor: "text-red-600 dark:text-red-400",
    countBg: "bg-red-100 dark:bg-red-500/15",
    countColor: "text-red-600 dark:text-red-400",
    headerBg: "bg-gradient-to-br from-red-50 to-white dark:from-red-500/[0.05] dark:to-transparent",
    headerBorder: "border-red-100 dark:border-red-500/[0.08]",
    dotColor: "bg-red-400",
    valueBg: "bg-red-50 dark:bg-red-500/10",
    valueColor: "text-red-600 dark:text-red-400",
  },
  improvements: {
    title: "All Improvements",
    subtitle: "Compliance and operational readiness improvements this period",
    icon: TrendingUp,
    iconBg: "bg-emerald-100 dark:bg-emerald-500/15",
    iconColor: "text-emerald-600 dark:text-emerald-400",
    countBg: "bg-emerald-100 dark:bg-emerald-500/15",
    countColor: "text-emerald-600 dark:text-emerald-400",
    headerBg: "bg-gradient-to-br from-emerald-50 to-white dark:from-emerald-500/[0.05] dark:to-transparent",
    headerBorder: "border-emerald-100 dark:border-emerald-500/[0.08]",
    dotColor: "bg-emerald-400",
    valueBg: "bg-emerald-50 dark:bg-emerald-500/10",
    valueColor: "text-emerald-600 dark:text-emerald-400",
  },
  stable: {
    title: "All Stable Areas",
    subtitle: "Core compliance areas that remained stable throughout the period",
    icon: Minus,
    iconBg: "bg-amber-100 dark:bg-amber-500/15",
    iconColor: "text-amber-600 dark:text-amber-400",
    countBg: "bg-amber-100 dark:bg-amber-500/15",
    countColor: "text-amber-600 dark:text-amber-400",
    headerBg: "bg-gradient-to-br from-amber-50 to-white dark:from-amber-500/[0.05] dark:to-transparent",
    headerBorder: "border-amber-100 dark:border-amber-500/[0.08]",
    dotColor: "bg-amber-400",
    valueBg: "bg-amber-50 dark:bg-amber-500/10",
    valueColor: "text-amber-600 dark:text-amber-400",
  },
  regressed: {
    title: "Top POA&M Risks",
    subtitle: "POA&M items that need owners and remediation plans",
    icon: ShieldAlert,
    iconBg: "bg-orange-100 dark:bg-orange-500/15",
    iconColor: "text-orange-600 dark:text-orange-400",
    countBg: "bg-orange-100 dark:bg-orange-500/15",
    countColor: "text-orange-600 dark:text-orange-400",
    headerBg: "bg-gradient-to-br from-orange-50 to-white dark:from-orange-500/[0.05] dark:to-transparent",
    headerBorder: "border-orange-100 dark:border-orange-500/[0.08]",
    dotColor: "bg-orange-400",
    valueBg: "bg-orange-50 dark:bg-orange-500/10",
    valueColor: "text-orange-600 dark:text-orange-400",
  },
  inactive: {
    title: "Inactive Users",
    subtitle: "Users with no recent activity in the period",
    icon: Users,
    iconBg: "bg-amber-100 dark:bg-amber-500/15",
    iconColor: "text-amber-600 dark:text-amber-400",
    countBg: "bg-amber-100 dark:bg-amber-500/15",
    countColor: "text-amber-600 dark:text-amber-400",
    headerBg: "bg-gradient-to-br from-amber-50 to-white dark:from-amber-500/[0.05] dark:to-transparent",
    headerBorder: "border-amber-100 dark:border-amber-500/[0.08]",
    dotColor: "bg-amber-400",
    valueBg: "bg-amber-50 dark:bg-amber-500/10",
    valueColor: "text-amber-600 dark:text-amber-400",
  },
} as const;

/* Reformat any embedded ISO date (YYYY-MM-DD) inside a metric string to
   DD/MM/YYYY, leaving the surrounding text (e.g. "Remediated") untouched. */
const formatMetricDates = (value?: string | null): string =>
  (value ?? "-").replace(
    /(\d{4})-(\d{2})-(\d{2})/g,
    (_m, y, mo, d) => `${d}/${mo}/${y}`,
  );

/* Small labeled tag — "SSP :" followed by the plan name. Rendered wherever an
   application/finding row carries an sspName (drawer rows, tables, actions). */
function SspTag({ name, className = "" }: { name: string; className?: string }) {
  return (
    <span
      title={name}
      className={`mt-0.5 flex w-fit max-w-full items-center gap-1 break-words rounded-md border border-indigo-200 bg-indigo-50 px-1.5 py-0.5 text-[11px] leading-snug dark:border-indigo-400/30 dark:bg-indigo-500/10 ${className}`}
    >
      <span className="shrink-0 font-semibold text-[#475569] dark:text-white/60">SSP :</span>
      <span className="break-words font-semibold text-indigo-600 dark:text-indigo-300">{name}</span>
    </span>
  );
}

/* Columns for the "What Improved" drill-down table — shows each finding with
   its before/after state side by side. */
const IMPROVED_COLUMNS: Column<InsightDetail>[] = [
  {
    key: "label",
    header: "Finding",
    align: "left",
    headerAlign: "center",
    ellipsis: 2,
    tooltip: (d) => d.label,
    render: (d) => (
      <div className="min-w-0">
        <span className="font-semibold text-[#1e293b] dark:text-white/90">{d.label}</span>
        {d.sspName && <SspTag name={d.sspName} className="flex" />}
      </div>
    ),
  },
  {
    key: "previousMetric",
    header: "Before",
    align: "left",
    headerAlign: "center",
    render: (d) => (
      <span className="text-[#64748b] dark:text-white/60">{formatMetricDates(d.previousMetric)}</span>
    ),
  },
  {
    key: "metric",
    header: "After",
    align: "left",
    headerAlign: "center",
    render: (d) => (
      <span className="font-medium text-emerald-600 dark:text-emerald-400">{formatMetricDates(d.metric)}</span>
    ),
  },
];

/* Columns for the "Top POA&M Risks" drill-down table — each finding with its
   severity badge and remediation status. */
const WORSEPOAMS_COLUMNS: Column<InsightDetail>[] = [
  {
    key: "label",
    header: "Finding",
    align: "left",
    ellipsis: 2,
    tooltip: (d) => d.label,
    render: (d) => (
      <div className="min-w-0">
        <span className="font-medium text-[#1e293b] dark:text-white/90">{d.label}</span>
        {d.sspName && <SspTag name={d.sspName} className="flex" />}
      </div>
    ),
  },
  {
    key: "metric",
    header: "Severity",
    align: "left",
    render: (d) => {
      const sev = d.metric?.trim().toLowerCase();
      const cls = sev ? SEVERITY_BADGE[sev] : undefined;
      if (!cls) return <span className="text-[#94a3b8] dark:text-white/30">-</span>;
      return (
        <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-bold capitalize ${cls}`}>
          {sev}
        </span>
      );
    },
  },
  {
    key: "status",
    header: "Status",
    align: "left",
    ellipsis: 2,
    tooltip: (d) => humanizeStatus(d.status),
    render: (d) => (
      <span className="text-[#64748b] dark:text-white/60">{humanizeStatus(d.status)}</span>
    ),
  },
];

/** A redesigned, clickable insight card-row. Big metric, title + subtitle,
 *  priority/category chips, and an item count that drills into the drawer. */
function SectionItemRow({
  item,
  type,
  onClick,
}: {
  item: InsightItem;
  type: CardSectionType;
  onClick: () => void;
}) {
  const t = SECTION_THEME[type];
  return (
    <button
      onClick={onClick}
      className={`group w-full text-left rounded-xl border border-[#eef2f6] dark:border-white/[0.05] bg-[#fafbfc] dark:bg-white/[0.02] p-3 transition-all hover:shadow-sm ${t.rowHover}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-[#0f172a] dark:text-white leading-snug break-words">
            {item.title}
          </p>
          <p className="mt-1 text-xs text-[#64748b] dark:text-white/60 leading-snug break-words">
            {item.subtitle}
          </p>
        </div>
        {item.metric && (
          <span className="shrink-0 break-normal whitespace-nowrap text-sm font-bold leading-none tabular-nums text-[#0f172a] dark:text-white">
            {item.metric}
          </span>
        )}
      </div>

      {item.details.length > 0 && (
        <div className="mt-3 flex items-center justify-end">
          <span className={`inline-flex items-center gap-1 text-xs font-semibold ${t.textMuted}`}>
            View details
            <ChevronRight className="size-3.5 transition-transform group-hover:translate-x-0.5" />
          </span>
        </div>
      )}
    </button>
  );
}

/** Pulsing placeholder shown while the insights endpoint is loading. */
function CardRowSkeleton() {
  return (
    <div className="rounded-xl border border-[#eef2f6] bg-[#fafbfc] p-3.5 dark:border-white/[0.05] dark:bg-white/[0.02]">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1 space-y-2">
          <div className="h-3.5 w-2/3 animate-pulse rounded bg-foreground/[0.10]" />
          <div className="h-2.5 w-full animate-pulse rounded bg-foreground/[0.06]" />
        </div>
        <div className="h-6 w-7 animate-pulse rounded bg-foreground/[0.10]" />
      </div>
      <div className="mt-3 flex justify-end">
        <div className="h-2.5 w-14 animate-pulse rounded bg-foreground/[0.06]" />
      </div>
    </div>
  );
}

/** Full-page loading screen shown on the very first load (before any data). */
function InsightsLoadingScreen() {
  return (
    <div className="flex flex-col items-center justify-center gap-3 rounded-2xl border border-[#e2e8f0] bg-white px-6 py-20 dark:border-white/[0.06] dark:bg-[#131320]">
      <Loader2 className="size-7 animate-spin text-indigo-500 dark:text-indigo-400" />
      <p className="text-sm font-semibold text-[#0f172a] dark:text-white">Loading executive insights</p>
      <p className="text-xs text-[#64748b] dark:text-white/50">Gathering the latest compliance summary across your applications</p>
    </div>
  );
}

/** Error state shown when the insights endpoint fails. Offers a retry. */
function InsightsErrorState({ message, onRetry, retrying }: { message: string; onRetry: () => void; retrying: boolean }) {
  return (
    <div className="flex flex-col items-center justify-center gap-3 rounded-2xl border border-red-100 bg-gradient-to-br from-red-50 to-white px-6 py-16 text-center dark:border-red-500/[0.12] dark:from-red-500/[0.05] dark:to-[#131320]">
      <div className="flex size-12 items-center justify-center rounded-full bg-red-100 text-red-600 dark:bg-red-500/15 dark:text-red-400">
        <AlertTriangle className="size-6" />
      </div>
      <p className="text-sm font-bold text-[#0f172a] dark:text-white">Unable to load executive insights</p>
      <p className="max-w-md break-words text-xs leading-relaxed text-[#64748b] dark:text-white/55">{message}</p>
      <button
        onClick={onRetry}
        disabled={retrying}
        className="mt-1 inline-flex items-center gap-1.5 rounded-lg border border-red-200 bg-white px-3.5 py-2 text-xs font-semibold text-red-600 transition-colors hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-red-500/25 dark:bg-white/[0.04] dark:text-red-400 dark:hover:bg-red-500/10"
      >
        <RefreshCw className={`size-3.5 ${retrying ? "animate-spin" : ""}`} />
        {retrying ? "Retrying" : "Try again"}
      </button>
    </div>
  );
}

/** One of the three insight cards (Worse / Improved / Stable). */
function InsightCard({
  section,
  loading,
  onOpenItem,
  onOpenAll,
}: {
  section: InsightSection;
  loading: boolean;
  onOpenItem: (item: InsightItem) => void;
  onOpenAll: () => void;
}) {
  const cardType = section.type as CardSectionType;
  const t = SECTION_THEME[cardType];
  const Icon = t.icon;
  return (
    <div className="flex flex-col overflow-hidden rounded-2xl border border-[#e2e8f0] bg-white dark:border-white/[0.06] dark:bg-[#131320]">
      {/* header */}
      <div className={`flex items-center justify-between px-4 pb-3 pt-3.5 ${t.headerBg} border-b ${t.headerBorder}`}>
        <div className="flex items-center gap-2">
          <div className={`flex size-7 items-center justify-center rounded-lg ${t.iconBox}`}>
            <Icon className="size-3.5" />
          </div>
          <span className={`text-xs font-bold tracking-widest ${t.text}`}>
            {section.label}
          </span>
        </div>
        {loading ? (
          <span className="size-5 animate-pulse rounded-full bg-foreground/10" />
        ) : (
          <span className="flex size-5 items-center justify-center rounded-full bg-[#f1f5f9] text-[10px] font-bold text-[#0f172a] dark:bg-white/10 dark:text-white">
            {section.count}
          </span>
        )}
      </div>

      {/* body */}
      <div className="flex-1 space-y-2 px-3.5 py-3">
        {loading ? (
          <>
            <CardRowSkeleton />
            <CardRowSkeleton />
          </>
        ) : section.items.length === 0 ? (
          <p className="py-6 text-center text-xs text-[#64748b] dark:text-white/50">{t.emptyText}</p>
        ) : (
          section.items.map((item, i) => (
            <SectionItemRow key={i} item={item} type={cardType} onClick={() => onOpenItem(item)} />
          ))
        )}
      </div>

      {/* footer */}
      {!loading && section.items.length > 0 && (
        <div className="border-t border-[#f1f5f9] px-4 py-3 dark:border-white/[0.04]">
          <button
            onClick={onOpenAll}
            className={`flex items-center gap-1 text-[13px] font-semibold hover:underline ${t.textMuted}`}
          >
            {t.viewAll} <ArrowRight className="size-3.5" />
          </button>
        </div>
      )}
    </div>
  );
}


/* ── Key Metrics: grouped sections driven by GET /dashboard/insights/counts ──
   Each API group becomes a section heading; each field present becomes a card.
   Fields absent from the response are skipped, and empty groups don't render. */
const COUNT_TONES: Record<string, { iconBox: string; value: string; border: string; accent: string }> = {
  red:     { iconBox: "bg-red-100 text-red-600 dark:bg-red-500/15 dark:text-red-400",           value: "text-red-600 dark:text-red-400",           border: "border-red-100 bg-gradient-to-br from-red-50 to-white dark:border-red-500/10 dark:from-red-500/[0.05] dark:to-[#131320]",           accent: "border-l-red-400 dark:border-l-red-500/70" },
  rose:    { iconBox: "bg-rose-100 text-rose-600 dark:bg-rose-500/15 dark:text-rose-400",        value: "text-rose-600 dark:text-rose-400",         border: "border-rose-100 bg-gradient-to-br from-rose-50 to-white dark:border-rose-500/10 dark:from-rose-500/[0.05] dark:to-[#131320]",        accent: "border-l-rose-400 dark:border-l-rose-500/70" },
  amber:   { iconBox: "bg-amber-100 text-amber-600 dark:bg-amber-500/15 dark:text-amber-400",    value: "text-amber-600 dark:text-amber-400",       border: "border-amber-100 bg-gradient-to-br from-amber-50 to-white dark:border-amber-500/10 dark:from-amber-500/[0.05] dark:to-[#131320]",     accent: "border-l-amber-400 dark:border-l-amber-500/70" },
  teal:    { iconBox: "bg-teal-100 text-teal-600 dark:bg-teal-500/15 dark:text-teal-400",        value: "text-teal-600 dark:text-teal-400",         border: "border-teal-100 bg-gradient-to-br from-teal-50 to-white dark:border-teal-500/10 dark:from-teal-500/[0.05] dark:to-[#131320]",        accent: "border-l-teal-400 dark:border-l-teal-500/70" },
  sky:     { iconBox: "bg-sky-100 text-sky-600 dark:bg-sky-500/15 dark:text-sky-400",            value: "text-sky-600 dark:text-sky-400",           border: "border-sky-100 bg-gradient-to-br from-sky-50 to-white dark:border-sky-500/10 dark:from-sky-500/[0.05] dark:to-[#131320]",           accent: "border-l-sky-400 dark:border-l-sky-500/70" },
  blue:    { iconBox: "bg-blue-100 text-blue-600 dark:bg-blue-500/15 dark:text-blue-400",        value: "text-blue-600 dark:text-blue-400",         border: "border-blue-100 bg-gradient-to-br from-blue-50 to-white dark:border-blue-500/10 dark:from-blue-500/[0.05] dark:to-[#131320]",        accent: "border-l-blue-400 dark:border-l-blue-500/70" },
  green:   { iconBox: "bg-green-100 text-green-600 dark:bg-green-500/15 dark:text-green-400",     value: "text-green-600 dark:text-green-400",       border: "border-green-100 bg-gradient-to-br from-green-50 to-white dark:border-green-500/10 dark:from-green-500/[0.05] dark:to-[#131320]",     accent: "border-l-green-400 dark:border-l-green-500/70" },
  emerald: { iconBox: "bg-emerald-100 text-emerald-600 dark:bg-emerald-500/15 dark:text-emerald-400", value: "text-emerald-600 dark:text-emerald-400", border: "border-emerald-100 bg-gradient-to-br from-emerald-50 to-white dark:border-emerald-500/10 dark:from-emerald-500/[0.05] dark:to-[#131320]", accent: "border-l-emerald-400 dark:border-l-emerald-500/70" },
  violet:  { iconBox: "bg-violet-100 text-violet-600 dark:bg-violet-500/15 dark:text-violet-400", value: "text-violet-600 dark:text-violet-400",     border: "border-violet-100 bg-gradient-to-br from-violet-50 to-white dark:border-violet-500/10 dark:from-violet-500/[0.05] dark:to-[#131320]",   accent: "border-l-violet-400 dark:border-l-violet-500/70" },
  slate:   { iconBox: "bg-slate-100 text-slate-600 dark:bg-white/[0.08] dark:text-white/70",      value: "text-slate-600 dark:text-white/80",        border: "border-slate-200 bg-gradient-to-br from-slate-50 to-white dark:border-white/[0.08] dark:from-white/[0.03] dark:to-[#131320]",         accent: "border-l-slate-400 dark:border-l-white/30" },
  /* ── App brand palette (matches the sidebar nav accents) — no red/amber ── */
  brandIndigo: { iconBox: "bg-[#6366F1]/12 text-[#6366F1] dark:bg-[#6366F1]/15 dark:text-[#A5B4FC]", value: "text-[#6366F1] dark:text-[#A5B4FC]", border: "border-[#6366F1]/20 bg-gradient-to-br from-[#6366F1]/[0.05] to-white dark:border-[#6366F1]/20 dark:from-[#6366F1]/[0.07] dark:to-[#131320]", accent: "border-l-[#6366F1] dark:border-l-[#818CF8]" },
  brandViolet: { iconBox: "bg-[#A78BFA]/14 text-[#7C5CFF] dark:bg-[#A78BFA]/15 dark:text-[#A78BFA]", value: "text-[#7C5CFF] dark:text-[#A78BFA]", border: "border-[#A78BFA]/25 bg-gradient-to-br from-[#A78BFA]/[0.06] to-white dark:border-[#A78BFA]/20 dark:from-[#A78BFA]/[0.07] dark:to-[#131320]", accent: "border-l-[#A78BFA]" },
  brandBlue:   { iconBox: "bg-[#388BFD]/12 text-[#388BFD] dark:bg-[#388BFD]/15 dark:text-[#60A5FA]", value: "text-[#388BFD] dark:text-[#60A5FA]", border: "border-[#388BFD]/20 bg-gradient-to-br from-[#388BFD]/[0.05] to-white dark:border-[#388BFD]/20 dark:from-[#388BFD]/[0.07] dark:to-[#131320]", accent: "border-l-[#388BFD] dark:border-l-[#60A5FA]" },
  brandCyan:   { iconBox: "bg-[#22D3EE]/14 text-[#0E9BB4] dark:bg-[#22D3EE]/15 dark:text-[#22D3EE]", value: "text-[#0E9BB4] dark:text-[#22D3EE]", border: "border-[#22D3EE]/25 bg-gradient-to-br from-[#22D3EE]/[0.06] to-white dark:border-[#22D3EE]/20 dark:from-[#22D3EE]/[0.07] dark:to-[#131320]", accent: "border-l-[#22D3EE]" },
  brandGreen:  { iconBox: "bg-[#34D399]/14 text-[#0E9F6E] dark:bg-[#34D399]/15 dark:text-[#34D399]", value: "text-[#0E9F6E] dark:text-[#34D399]", border: "border-[#34D399]/25 bg-gradient-to-br from-[#34D399]/[0.06] to-white dark:border-[#34D399]/20 dark:from-[#34D399]/[0.07] dark:to-[#131320]", accent: "border-l-[#34D399]" },
  brandRed:    { iconBox: "bg-[#F96C6C]/14 text-[#E1494A] dark:bg-[#F96C6C]/15 dark:text-[#F96C6C]", value: "text-[#E1494A] dark:text-[#F96C6C]", border: "border-[#F96C6C]/25 bg-gradient-to-br from-[#F96C6C]/[0.06] to-white dark:border-[#F96C6C]/20 dark:from-[#F96C6C]/[0.07] dark:to-[#131320]", accent: "border-l-[#F96C6C]" },
  brandAmber:  { iconBox: "bg-[#F59E0C]/14 text-[#C77D06] dark:bg-[#F59E0C]/15 dark:text-[#F59E0C]", value: "text-[#C77D06] dark:text-[#F59E0C]", border: "border-[#F59E0C]/25 bg-gradient-to-br from-[#F59E0C]/[0.06] to-white dark:border-[#F59E0C]/20 dark:from-[#F59E0C]/[0.07] dark:to-[#131320]", accent: "border-l-[#F59E0C]" },
};

/* Preferred group order + display titles + a default tone per group. Any group
   NOT listed here still renders (appended after), so new API groups appear too. */
const GROUP_ORDER = [
  "compliancePosture", "riskAndRemediation", "assessmentHealth",
  "governanceAndDocuments", "assetsAndCoverage", "operationalHealth",
];
const GROUP_TITLES: Record<string, string> = {
  compliancePosture: "Compliance Posture",
  riskAndRemediation: "Risk & Remediation",
  assessmentHealth: "Assessment Health",
  governanceAndDocuments: "Governance & Documents",
  assetsAndCoverage: "Assets & Coverage",
  operationalHealth: "Operational Health",
};
const GROUP_TONE: Record<string, string> = {
  compliancePosture: "brandGreen",
  riskAndRemediation: "brandRed",
  assessmentHealth: "brandAmber",
  governanceAndDocuments: "brandBlue",
  assetsAndCoverage: "brandCyan",
  operationalHealth: "brandViolet",
};
/* Header icon per group (shown in each Key Metrics panel header). */
const GROUP_ICON: Record<string, typeof Activity> = {
  compliancePosture: FileCheck2,
  riskAndRemediation: ShieldAlert,
  assessmentHealth: ClipboardCheck,
  governanceAndDocuments: FileText,
  assetsAndCoverage: Server,
  operationalHealth: Activity,
};
/* Nice label/icon/tone per known field. Unknown fields fall back to an
   auto-generated label + the group tone, so new counts render without changes. */
interface FieldMeta { label: string; sub?: string; Icon?: typeof Activity; tone?: string; suffix?: string }
const FIELD_META: Record<string, FieldMeta> = {
  criticalPoamCount:             { label: "Critical POA&Ms", sub: "Open critical findings", Icon: ShieldAlert, tone: "red" },
  recentCriticalPoamCount:       { label: "Recent Critical POA&M", sub: "Reported in the last week", Icon: ShieldAlert, tone: "red" },
  overduePoamCount:              { label: "Overdue POA&Ms", sub: "Past scheduled completion", Icon: CalendarClock, tone: "rose" },
  avgRemediationDays:            { label: "Avg Remediation Time", sub: "Across open POA&M items", Icon: Clock, tone: "amber", suffix: " days" },
  highRiskApplicationsCount:     { label: "High Risk Applications", sub: "Applications with elevated risk", Icon: Activity, tone: "red" },
  completedAssessmentPlansCount: { label: "Assessments Completed", sub: "Assessment plans finished", Icon: ClipboardCheck, tone: "teal" },
  assessmentInProgress:          { label: "Assessments In Progress", sub: "Currently being executed", Icon: Activity, tone: "sky" },
  activeAssessmentsCount:        { label: "Active Assessments", sub: "Approved and running", Icon: Activity, tone: "sky" },
  approvalQueue:                 { label: "Assessments In Review", sub: "Awaiting sign-off", Icon: ListChecks, tone: "amber" },
  overdueAssessmentPlansCount:   { label: "Assessments Overdue", sub: "Past due date", Icon: CalendarClock, tone: "rose" },
  inReviewAssessmentPlansCount:  { label: "Assessments In Review", sub: "Awaiting review sign-off", Icon: Clock, tone: "amber" },
  reviewBottlenecksCount:        { label: "Review Bottlenecks", sub: "Stuck in review over a week", Icon: Clock, tone: "amber" },
  draftDocumentsCount:           { label: "Draft Documents", sub: "Work in progress", Icon: FileText, tone: "slate" },
  inReviewDocumentsCount:        { label: "Documents In Review", sub: "Pending approval", Icon: FileText, tone: "blue" },
  approvedDocumentsCount:        { label: "Approved Documents", sub: "Signed-off documents", Icon: FileCheck2, tone: "green" },
  unmappedControlsCount:         { label: "Unmapped Controls", sub: "Not linked to a catalog", Icon: FileText, tone: "amber" },
  unmappedControlsPercent:       { label: "Unmapped Controls Rate", sub: "Share of all controls", Icon: FileText, tone: "amber", suffix: "%" },
  unusedCatalogControlsCount:    { label: "Unused Catalog Controls", sub: "Available but not applied", Icon: FileText, tone: "slate" },
  totalApplicationsCount:        { label: "Total Applications", sub: "Onboarded to the platform", Icon: AppWindow, tone: "sky" },
  linkedComponentsCount:         { label: "Linked Components", sub: "Assets mapped to applications", Icon: Server, tone: "sky" },
  cloudAssetsCount:              { label: "Cloud Assets", sub: "Discovered in the cloud", Icon: Cloud, tone: "blue" },
  onPremAssetsCount:             { label: "On-prem Assets", sub: "Discovered on-premises", Icon: Server, tone: "teal" },
  remediatedPoamCount:           { label: "Findings Remediated", sub: "Resolved to date", Icon: CheckCircle2, tone: "emerald" },
  inactiveUsersCount:            { label: "Inactive Users", sub: "No recent activity", Icon: Users, tone: "violet" },
  supportingEvidenceCount:       { label: "Supporting Evidence", sub: "Evidence artifacts collected", Icon: Paperclip, tone: "violet" },
};

/* Which Governance area a metric's records live in — surfaced as a small
   "source" chip on the Key Metrics card so users know where to go to manage
   them (mirrors the sidebar's Governance nav: Content Registry / Catalog /
   Profile / Control Mapping). Fields not listed here render no source chip. */
const FIELD_SOURCE: Record<string, string> = {
  draftDocumentsCount:        "Content Registry",
  inReviewDocumentsCount:     "Content Registry",
  approvedDocumentsCount:     "Content Registry",
  unusedCatalogControlsCount: "Catalog",
  unmappedControlsCount:      "Control Mapping",
  unmappedControlsPercent:    "Control Mapping",
};

/* Auto-label an unknown field key: "avgRemediationDays" → "Avg Remediation Days",
   "criticalPoamCount" → "Critical POA&M" (drops trailing "Count", fixes acronyms). */
function prettyCountKey(key: string): string {
  const words = key
    .replace(/Count$/, "")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => {
      const u = w.toUpperCase();
      if (["POAM", "SSP", "SLA", "CMDB", "AI", "IT"].includes(u)) return u === "POAM" ? "POA&M" : u;
      return w.charAt(0).toUpperCase() + w.slice(1);
    });
  return words.join(" ");
}

/* Plain-language descriptions shown in the info-icon tooltip on each Key
   Metrics card. Keyed by the raw API field name. */
const FIELD_DESC: Record<string, string> = {
  criticalPoamCount: "How many open, critical-severity issues still need fixing.",
  overduePoamCount: "Issues whose fix deadline has already passed.",
  avgRemediationDays: "On average, how many days it takes to fully fix an issue.",
  highRiskApplicationsCount: "How many applications are currently failing compliance.",
  recentCriticalPoamCount: "New critical issues raised in the last 7 days.",
  completedAssessmentPlansCount: "Assessments that have finished their full review.",
  assessmentInProgress: "Assessments approved and currently running.",
  activeAssessmentsCount: "Assessments approved and currently running.",
  approvalQueue: "Assessments waiting to be reviewed and approved.",
  overdueAssessmentPlansCount: "Assessments whose scheduled end date has passed without finishing.",
  reviewBottlenecksCount: "Assessments stuck waiting in review for more than a week.",
  inReviewAssessmentPlansCount: "Assessments awaiting review sign-off.",
  draftDocumentsCount: "Documents still being written (draft stage).",
  inReviewDocumentsCount: "Documents currently under review.",
  approvedDocumentsCount: "Documents that have been approved.",
  unusedCatalogControlsCount: "Controls that no assessment plan is testing yet.",
  unmappedControlsCount: "Controls not linked to any other framework's control.",
  unmappedControlsPercent: "What share of all controls are unmapped (as a %).",
  totalApplicationsCount: "Total number of applications being tracked.",
  onPremAssetsCount: "Assets hosted on-premises (your own servers).",
  cloudAssetsCount: "Assets hosted in the cloud.",
  linkedComponentsCount: "Distinct software components tied to an application.",
  remediatedPoamCount: "Issues that have been fully resolved and closed.",
  inactiveUsersCount: "Active accounts that haven't logged in for over 30 days.",
  supportingEvidenceCount: "Evidence files uploaded that actually contain a document/file.",
};
/* Descriptions for each section header (API group), shown in its info tooltip. */
const GROUP_DESC: Record<string, string> = {
  compliancePosture: "Overall control coverage and compliance standing.",
  riskAndRemediation: "Open risks and how quickly they're being fixed.",
  assessmentHealth: "Status of assessments across their lifecycle.",
  governanceAndDocuments: "Document workflow and control coverage.",
  assetsAndCoverage: "What's being tracked: apps, assets, and components.",
  operationalHealth: "Day-to-day activity and hygiene signals.",
};

/* Key Metrics layout config — persisted in localStorage so the user's choice
   survives refresh and re-login (same browser). */
const KM_CONFIG_KEY = "rego.execInsights.keyMetrics.v1";
const DEFAULT_KM_CONFIG: KeyMetricsConfig = { columns: 4, hidden: [], order: {} };
function loadKmConfig(): KeyMetricsConfig {
  try {
    const raw = localStorage.getItem(KM_CONFIG_KEY);
    if (raw) return { ...DEFAULT_KM_CONFIG, ...(JSON.parse(raw) as Partial<KeyMetricsConfig>) };
  } catch {
    /* ignore corrupt/unavailable storage → defaults */
  }
  return DEFAULT_KM_CONFIG;
}
function saveKmConfig(c: KeyMetricsConfig) {
  try {
    localStorage.setItem(KM_CONFIG_KEY, JSON.stringify(c));
  } catch {
    /* storage unavailable — non-fatal */
  }
}
/* col count → responsive grid class */
const KM_GRID_COLS: Record<number, string> = {
  2: "grid-cols-1 sm:grid-cols-2",
  3: "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3",
  4: "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4",
  5: "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5",
};

/** The `PageView` each tab publishes to the AI Companion. Module scope so the effect below has a
    stable dependency and the union is checked against `uiContextStore`'s `PageView` here, not there. */
const PAGE_VIEW_BY_TAB = {
  insights: "INSIGHTS_SUMMARY",
  dashboard: "INSIGHTS_METRICS",
  quadrant: "INSIGHTS_QUADRANT",
} as const;

/* ── Main component ─────────────────────────────────────────────────────── */
export function ExecutiveInsights() {
  /* Top-level view: Executive Insights (default), the role Dashboard, or Quadrant. */
  const [activeTab, setActiveTab] = useState<"insights" | "dashboard" | "quadrant">("insights");

  /* Role drives the Dashboard tab's header text (e.g. "System Admin Dashboard"). Declared here rather
     than with the rest of the header state below because the effect that follows reads both, and a
     dependency array is evaluated during render — referencing them before their `const` would be a
     temporal-dead-zone error, not a stale value. */
  const userRoles = useAuthStore((s) => s.user?.roles) ?? [];
  /* Per-tab page header (title + subtitle) shown above the tab bar. */
  const tabHeader =
    activeTab === "dashboard"
      ? { title: roleDashboardTitle(userRoles), subtitle: roleDashboardSubtitle(userRoles) }
      : activeTab === "quadrant"
        ? { title: "Executive Risk Quadrant", subtitle: "Risk & Compliance Posture" }
        : {
            title: "Executive Insights",
            subtitle:
              "Real-time summary of your compliance posture and key changes across your applications.",
          };

  /* Publish which tab is open, for the AI Companion. This is the application's landing page and it
     renders three whole dashboards behind one unchanging path, so without this the companion cannot
     tell a user reading the insights list from one staring at the risk quadrant, and offers the same
     questions to both. The last two are not corners of this screen — they are other screens' content,
     and the backend maps them across (see `Screen.forView`). Cleared on unmount like every other page.

     The heading goes with it, because this page is the one place that knows it: the Dashboard tab's
     title is composed from the viewer's own roles, so it reads "Administrator Dashboard" for one user
     and "GRC Dashboard" for another, and no table anywhere else could hold it. */
  useEffect(() => {
    useUiContextStore
      .getState()
      .setPageContext(PAGE_VIEW_BY_TAB[activeTab], null, tabHeader.title);
    return () => useUiContextStore.getState().clearPageContext();
  }, [activeTab, tabHeader.title]);

  /* DOM slot in the tab-bar row that the Dashboard / Quadrant views portal their
     header controls (filters, refresh, group picker) into, so they sit on the
     same row as the tabs. Shared — only one tab is active at a time. */
  const [tabControlsSlot, setTabControlsSlot] = useState<HTMLDivElement | null>(null);
  /* ── Organization filter ─────────────────────────────────────────────────
     Driven by GET /org-units?root_only=true&flat=false.
     The root GROUP node is shown as a fixed label (e.g. "DevOpsLabs"); the
     Organization multi-select lists the ORGANIZATION nodes under that group. */
  const [orgTree, setOrgTree] = useState<OrgUnit[]>([]);
  /* Draft org selection edited by the dropdown. */
  const [selectedOrgIds, setSelectedOrgIds] = useState<string[]>([]);
  /* Applied selection — only this triggers the API call (on Apply click). */
  const [appliedOrgIds, setAppliedOrgIds] = useState<string[]>([]);

  /* True once the org tree fetch settles — insights wait for this so the first
     load can include the group id. */
  const [orgTreeLoaded, setOrgTreeLoaded] = useState(false);

  useEffect(() => {
    getOrgUnitTree()
      .then((res) => setOrgTree(res?.data ?? []))
      .catch((e) => console.error("Failed to load org units", e))
      .finally(() => setOrgTreeLoaded(true));
  }, []);

  /* The (first) root GROUP node — shown as a fixed context label + default filter. */
  const group = orgTree.find((n) => n.orgType === "GROUP") ?? null;

  /* Organizations under that group (ORGANIZATION nodes, collected recursively). */
  const orgOptions = (() => {
    const acc: { value: string; label: string }[] = [];
    const walk = (nodes: OrgUnit[]) => {
      nodes.forEach((n) => {
        if (n.orgType === "ORGANIZATION") acc.push({ value: n.id, label: n.name.trim() });
        if (n.children?.length) walk(n.children);
      });
    };
    walk(group?.children ?? []);
    return acc;
  })();

  /* Resolve the API filter: selected orgs → orgId params; none → the group id. */
  const orgFilter = (orgIds: string[]): InsightsFilter =>
    orgIds.length ? { orgIds } : group ? { groupIds: [group.id] } : {};

  const applyFilter = () => setAppliedOrgIds(selectedOrgIds);
  /* Clear all → clear selection and immediately reload with the group id. */
  const clearOrgFilter = () => {
    setSelectedOrgIds([]);
    setAppliedOrgIds([]);
  };

  /* System Owner persona — that dashboard does not use the insights counts
     endpoint, so it is never called for this role. Same case/separator-
     insensitive role match DashboardRouter uses to pick the persona page. */
  const isSystemOwner = userRoles.some(
    (r) => r.toLowerCase().replace(/[\s_-]/g, "") === "systemowner",
  );
  /* Drawer drill-down: a section + the item(s) being inspected. */
  const [drawer, setDrawer] = useState<{ section: InsightSection; items: InsightItem[] } | null>(null);
  /* Which multi-item groups are expanded in the drawer (collapsed by default). */
  const [expandedGroups, setExpandedGroups] = useState<Set<number>>(new Set());
  const openDrill = (section: InsightSection, item: InsightItem) => {
    setExpandedGroups(new Set());
    setDrawer({ section, items: [item] });
  };
  const openAll = (section: InsightSection) => {
    setExpandedGroups(new Set());
    setDrawer({ section, items: section.items });
  };
  const closeDrawer = () => setDrawer(null);
  /* Slide-in panel listing every management action (when more than 8 exist). */
  const [showAllActions, setShowAllActions] = useState(false);
  const toggleGroup = (i: number) =>
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i);
      else next.add(i);
      return next;
    });

  /* Insights data — fetched from the API, empty scaffold until it loads. */
  const [insights, setInsights] = useState<ApplicationInsightsData>(EMPTY_INSIGHTS);
  const [insightsLoading, setInsightsLoading] = useState(true);
  /* Aggregate counts backing the Key Metrics cards (GET .../insights/counts). */
  const [counts, setCounts] = useState<InsightsCountsResponse | null>(null);
  /* Persisted Key Metrics layout (which cards show, their order, cards/row). */
  const [kmConfig, setKmConfig] = useState<KeyMetricsConfig>(loadKmConfig);
  const [configOpen, setConfigOpen] = useState(false);
  const applyKmConfig = (c: KeyMetricsConfig) => {
    setKmConfig(c);
    saveKmConfig(c);
    setConfigOpen(false);
  };
  /* Set when the endpoint fails — drives the inline error state with retry.
     null means "no error"; a non-empty string is shown to the user. */
  const [loadError, setLoadError] = useState<string | null>(null);
  /* True only while a manual retry is in flight (keeps prior content visible). */
  const [retrying, setRetrying] = useState(false);
  /* True before the first successful (or attempted) load completes. */
  const [hasLoaded, setHasLoaded] = useState(false);

  const loadInsights = async (isRetry = false, filter: InsightsFilter = {}) => {
    if (isRetry) setRetrying(true);
    else setInsightsLoading(true);
    setLoadError(null);
    try {
      const res = await fetchApplicationInsights(filter);
      /* Live API data only — no mock fallback. When the endpoint returns
         nothing, the empty scaffold drives the "No data available" state. */
      const next = res?.data ?? EMPTY_INSIGHTS;
      /* Normalize section key casing, then apply our hardcoded primary labels. */
      setInsights(withLocalLabels(normalizeInsights(next)));
      setHasLoaded(true);
      /* Aggregate counts — non-blocking; a failure here just leaves those
         cards showing "--" without breaking the rest of the page.
         Skipped entirely for the System Owner persona: that endpoint is not
         called anywhere in the System Owner Dashboard experience. */
      if (!isSystemOwner) {
        fetchInsightsCounts(filter)
          .then((c) => setCounts(c?.data ?? null))
          .catch((e) => {
            // Non-blocking: the rest of the page still renders. Log so a broken
            // counts endpoint is diagnosable instead of silently empty.
            console.error("Failed to load insights counts", e);
            setCounts(null);
          });
      }
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Failed to load executive insights. Please try again.";
      setLoadError(message);
      useToastStore.getState().showError("Unable to load insights", message);
    } finally {
      setInsightsLoading(false);
      setRetrying(false);
    }
  };

  /* Load once the org tree has settled (so the group id is known) and whenever
     the APPLIED org filter changes. Empty selection sends the group id. */
  useEffect(() => {
    if (!orgTreeLoaded) return;
    loadInsights(false, orgFilter(appliedOrgIds));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliedOrgIds, orgTreeLoaded]);

  const config = drawer ? DRAWER_CONFIG[SECTION_KEY[drawer.section.type]] : null;
  const drawerDetailCount = drawer
    ? drawer.items.reduce((sum, it) => sum + (it.details?.length ?? 0), 0)
    : 0;
  const drawerSingle = drawer?.items.length === 1 ? drawer.items[0] : null;

  /* ── Compliance Snapshot (text only, reuses live data, no new API) ─────────── */
  const worseCount = insights.worse.count;
  const improvedCount = insights.improved.count;
  const stableCount = insights.stable.count;

  /* Whether the API returned anything to show — drives the "No data available"
     empty state. True if any insights section or the aggregate counts have data. */
  const hasAnyData =
    worseCount > 0 ||
    improvedCount > 0 ||
    stableCount > 0 ||
    (insights.worsePoams?.count ?? 0) > 0 ||
    (insights.inactiveUsers?.count ?? 0) > 0 ||
    counts != null;

  const takeaways = [
    {
      key: "worse",
      title: "Needs Attention",
      desc: "Areas that require immediate action to avoid compliance risks.",
      count: worseCount,
      metricLabel: worseCount === 1 ? "issue" : "issues",
      Icon: AlertTriangle,
      text: "text-red-600 dark:text-red-400",
      circle: "bg-red-100 dark:bg-red-500/15 text-red-500 dark:text-red-400",
    },
    {
      key: "improved",
      title: "Posture Improved",
      desc: "Areas showing positive movement in compliance and operations.",
      count: improvedCount,
      metricLabel: improvedCount === 1 ? "improvement" : "improvements",
      Icon: TrendingUp,
      text: "text-emerald-600 dark:text-emerald-400",
      circle: "bg-emerald-100 dark:bg-emerald-500/15 text-emerald-500 dark:text-emerald-400",
    },
    {
      key: "stable",
      title: "Stable",
      desc: "Areas that remain stable with no significant changes.",
      count: stableCount,
      metricLabel: stableCount === 1 ? "Stable area" : "Stable areas",
      Icon: Minus,
      text: "text-amber-600 dark:text-amber-400",
      circle: "bg-amber-100 dark:bg-amber-500/15 text-amber-500 dark:text-amber-400",
    },
  ];

  /* Management Actions rows — driven entirely by the live topPoamItems.
     Only the first 8 are shown inline; the rest open in the slide-in panel. */
  const actions: ActionRow[] = (insights.topPoamItems ?? []).map(toActionRow);
  const visibleActions = actions.slice(0, MAX_VISIBLE_ACTIONS);
  const hasMoreActions = actions.length > MAX_VISIBLE_ACTIONS;

  /* Grouped Key-Metric sections — built dynamically from whatever the counts
     response contains. Known groups/fields get nice titles/labels/icons; any
     NEW group or field the backend adds renders automatically (auto-labelled).
     Each card carries its resolved style classes so the config modal can reuse
     the same look. `allGroups` = everything available; the config then decides
     which cards/order/columns actually render. */
  const countData = (counts ?? {}) as Record<
    string,
    Record<string, { value: number; description?: string; tooltip?: string } | string | undefined> | undefined
  >;
  const groupKeys = [
    ...GROUP_ORDER.filter((k) => k in countData),
    ...Object.keys(countData).filter((k) => !GROUP_ORDER.includes(k)),
  ]
    // Operational Health section hidden per request — remove this filter to show it again.
    .filter((k) => k !== "operationalHealth");
  const allGroups: ConfigGroup[] = groupKeys
    .map((groupKey) => {
      const g = countData[groupKey] ?? {};
      const cards: ConfigCard[] = Object.entries(g)
        // Field entries are objects with a numeric `value`; skip `title`/`description` strings.
        .filter(
          ([k, v]) =>
            k !== "title" && k !== "description" && !!v && typeof v === "object" && typeof v.value === "number",
        )
        .map(([key, raw]) => {
          const field = raw as { value: number; description?: string; tooltip?: string };
          const meta = FIELD_META[key];
          // Group tone drives the panel header, number colour, and accents so every
          // value inside a group shares one colour (e.g. all red in a red group).
          const tone = COUNT_TONES[GROUP_TONE[groupKey] ?? "brandIndigo"] ?? COUNT_TONES.brandIndigo;
          return {
            key,
            group: groupKey,
            label: meta?.label ?? prettyCountKey(key),
            // Prefer the copy the backend sends; fall back to the FE defaults.
            sub: field.description ?? meta?.sub ?? "",
            tooltip: field.tooltip ?? FIELD_DESC[key] ?? "",
            display: meta?.suffix ? `${field.value}${meta.suffix}` : String(field.value),
            Icon: meta?.Icon ?? Activity,
            iconBox: tone.iconBox,
            border: tone.border,
            valueColor: tone.value,
            accent: tone.accent,
          };
        });
      const rawTitle = typeof g.title === "string" ? g.title : undefined;
      const rawDesc = typeof g.description === "string" ? g.description : undefined;
      return {
        group: groupKey,
        title: rawTitle ?? GROUP_TITLES[groupKey] ?? prettyCountKey(groupKey),
        desc: rawDesc ?? GROUP_DESC[groupKey] ?? "",
        cards,
      };
    })
    .filter((s) => s.cards.length > 0);

  /* Apply the saved config: hide removed cards, honour saved order, keep any
     new (unconfigured) cards visible and appended. */
  const hiddenSet = new Set(kmConfig.hidden);
  const countSections = allGroups
    .map((g) => {
      const ord = kmConfig.order[g.group] ?? [];
      const inOrder = ord
        .map((k) => g.cards.find((c) => c.key === k))
        .filter((c): c is ConfigCard => !!c && !hiddenSet.has(c.key));
      const rest = g.cards.filter((c) => !hiddenSet.has(c.key) && !ord.includes(c.key));
      return { group: g.group, title: g.title, desc: g.desc, cards: [...inOrder, ...rest] };
    })
    .filter((s) => s.cards.length > 0);

  return (
    <div className="pb-6">
      {/* Content renders at full (100%) scale so its font sizes match the rest
          of the app. The drawer is rendered OUTSIDE this wrapper so the overlay
          stays at full scale too. */}
      <div className="space-y-4">
      {/* ── Page Header ─────────────────────────────────────────────────── */}
      {/* Title + subtitle stay fixed above the tab bar and switch with the
          selected tab; only the content below the tab bar changes. */}
      <div className="min-w-0">
        <h1 className="text-2xl font-bold tracking-tight text-[#0f172a] dark:text-white">{tabHeader.title}</h1>
        <p className="mt-1 text-sm text-[#475569] dark:text-white/55">{tabHeader.subtitle}</p>
      </div>

      {/* Full-width underline tab bar under the header. Always shown so the
          tabs stay fixed and only the content below the tabs changes. */}
      <div className="mt-1.5 flex w-full flex-wrap items-center justify-between gap-x-4 gap-y-1 border-b border-[#e2e8f0] pt-1 dark:border-white/[0.08]">
        <div className="flex max-w-full shrink-0 items-center gap-7 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {([
          { key: "insights",  label: "Summary" },
          { key: "dashboard", label: "Metrics" },
          { key: "quadrant",  label: "Control Quadrant" },
        ] as const).map((t) => {
          const active = activeTab === t.key;
          return (
            <button
              key={t.key}
              onClick={() => setActiveTab(t.key)}
              className={`group relative flex shrink-0 items-center gap-2 px-1 pb-3 pt-1.5 text-[15px] transition-colors ${
                active
                  ? "font-semibold text-blue-600 dark:text-blue-400"
                  : "font-medium text-slate-500 hover:text-slate-800 dark:text-white/55 dark:hover:text-white"
              }`}
            >
              {/* dot marker before the label */}
              <span
                className={`size-2 shrink-0 rounded-full transition-colors ${
                  active
                    ? "bg-blue-500 dark:bg-blue-400"
                    : "bg-slate-300 group-hover:bg-slate-400 dark:bg-white/25 dark:group-hover:bg-white/40"
                }`}
              />
              {t.label}
              {/* underline — full when active, faint on hover */}
              <span
                className={`pointer-events-none absolute inset-x-0 -bottom-px h-0.5 rounded-full bg-blue-500 transition-all duration-200 dark:bg-blue-400 ${
                  active ? "scale-x-100 opacity-100" : "scale-x-0 opacity-0 group-hover:scale-x-100 group-hover:opacity-40"
                }`}
              />
            </button>
          );
        })}
        </div>
        {/* Right-side slot — the Dashboard / Quadrant views portal their header
            controls here; the Insights tab renders its Org/Group filter here. */}
        <div ref={setTabControlsSlot} className="flex shrink-0 flex-wrap items-center gap-2 pb-2">
          {activeTab === "insights" && (
            <>
              {/* Fixed group context label (e.g. "DevOpsLabs"). */}
              {group && (
                <span className="inline-flex h-[38px] items-center gap-1.5 rounded-lg border border-indigo-300 bg-indigo-50 px-3 text-sm dark:border-indigo-400/40 dark:bg-indigo-500/10">
                  {/* <span className="text-[11px] font-semibold tracking-wider text-muted-foreground">Group</span> */}
                  <span className="font-semibold text-indigo-700 dark:text-indigo-300">{group.name}</span>
                </span>
              )}
              <MultiSelectDropdown
                placeholder="All Organizations"
                selectedNoun="Organization"
                options={orgOptions}  
                selected={selectedOrgIds}
                onChange={setSelectedOrgIds}
                onApply={applyFilter}
                onClearAll={clearOrgFilter}
              />
            </>
          )}
        </div>
      </div>

      {/* OLD filled-button design (kept for reference):
      <div className="flex shrink-0 items-center gap-2">
        <button onClick={() => setActiveTab("dashboard")} className="... bg-indigo-600 ...">
          <LayoutDashboard className="size-4" /> Dashboard
        </button>
        <button onClick={() => setActiveTab("quadrant")} className="... bg-indigo-600 ...">
          <LayoutGrid className="size-4" /> Quadrant
        </button>
      </div>
      */}

      {/* Dashboard / Quadrant views render in place. The page header above the
          tab bar already shows the title, so hide the child's own title here to
          avoid a duplicate heading (their controls stay visible). */}
      {activeTab === "dashboard" ? (
        <DashboardControlsSlotContext.Provider value={tabControlsSlot}>
          <DashboardRouter hideTitle />
        </DashboardControlsSlotContext.Provider>
      ) : activeTab === "quadrant" ? (
        <QuadrantControlsSlotContext.Provider value={tabControlsSlot}>
          <QuadrantDashboard hideTitle />
        </QuadrantControlsSlotContext.Provider>
      ) : /* First-load failure: show a retryable error in place of the data
          sections (the header stays so the page is never fully blank). Once
          data has loaded once, retry errors surface as a toast instead and the
          existing content stays visible. */
      loadError && !hasLoaded ? (
        <InsightsErrorState
          message={loadError}
          onRetry={() => loadInsights(true, orgFilter(appliedOrgIds))}
          retrying={retrying}
        />
      ) : !hasLoaded && insightsLoading ? (
        /* First-load spinner screen. */
        <InsightsLoadingScreen />
      ) : !hasAnyData ? (
        /* Loaded successfully but the API returned nothing — no mock data. */
        <div className="flex flex-col items-center justify-center py-24 text-center">
          <div className="flex size-14 items-center justify-center rounded-full bg-[#f1f5f9] dark:bg-white/[0.05]">
            <Activity className="size-6 text-[#94a3b8] dark:text-white/40" />
          </div>
          <p className="mt-4 text-sm font-semibold text-[#475569] dark:text-white/60">
            No data available
          </p>
          <p className="mt-1 text-xs text-[#94a3b8] dark:text-white/40">
            There are no insights for this period yet.
          </p>
        </div>
      ) : (
        <>
      {/* ── Compliance Snapshot (single panel, text only) ─────────────────────── */}
      <div className="rounded-2xl border border-[#e2e8f0] bg-white p-4 dark:border-white/[0.06] dark:bg-[#131320]">
        <h2 className="text-sm font-bold text-[#0f172a] dark:text-white">Compliance Snapshot</h2>
        <p className="mt-0.5 text-xs text-[#64748b] dark:text-white/55">Overall status of your Compliance Posture.</p>

        <div className="mt-5 flex flex-col gap-4 lg:flex-row lg:items-stretch lg:gap-0">
          {takeaways.map((c, i) => (
            <Fragment key={c.key}>
              {i > 0 && <div className="hidden w-px shrink-0 self-stretch bg-[#e2e8f0] dark:bg-white/[0.07] lg:block" />}
              <div className="flex flex-1 items-start gap-3.5 lg:px-6 lg:first:pl-0 lg:last:pr-0">
                <div className={`flex size-10 shrink-0 items-center justify-center rounded-full ${c.circle}`}>
                  <c.Icon className="size-5" />
                </div>
                <div className="flex h-full min-w-0 flex-col">
                  <p className={`text-sm font-bold ${c.text}`}>{c.title}</p>
                  <p className="mt-0.5 break-words text-xs leading-snug text-[#475569] dark:text-white/55">
                    {c.desc}
                  </p>
                  <div className="mt-auto flex items-baseline gap-1.5 pt-2.5">
                    {insightsLoading ? (
                      <span className="inline-block h-7 w-6 animate-pulse rounded bg-foreground/10" />
                    ) : (
                      <span className="break-normal text-xl font-bold leading-none tabular-nums text-[#0f172a] dark:text-white">
                        {c.count}
                      </span>
                    )}
                    <span className="break-normal text-xs font-medium text-[#475569] dark:text-white/55">
                      {c.metricLabel}
                    </span>
                  </div>
                </div>
              </div>
            </Fragment>
          ))}
        </div>
      </div>

      {/* ── Three insight cards ─────────────────────────────────────────── */}
      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
        <InsightCard section={insights.worse}    loading={insightsLoading} onOpenItem={(it) => openDrill(insights.worse, it)}    onOpenAll={() => openAll(insights.worse)} />
        <InsightCard section={insights.improved} loading={insightsLoading} onOpenItem={(it) => openDrill(insights.improved, it)} onOpenAll={() => openAll(insights.improved)} />
        <InsightCard section={insights.stable}   loading={insightsLoading} onOpenItem={(it) => openDrill(insights.stable, it)}   onOpenAll={() => openAll(insights.stable)} />
      </div>

      {/* ── Management Actions ───────────────────────────────────────────── */}
      <div className="flex flex-col overflow-hidden rounded-2xl border border-[#e2e8f0] bg-white dark:border-white/[0.06] dark:bg-[#131320]">
        <div className="flex items-center gap-2 px-5 pb-4 pt-5">
          <div className="flex size-7 shrink-0 items-center justify-center rounded-lg bg-violet-50 dark:bg-violet-500/15">
            <CheckCircle2 className="size-3.5 text-violet-600 dark:text-violet-400" />
          </div>
          <div className="flex min-w-0 items-baseline gap-2">
            <h3 className="text-sm font-bold text-[#0f172a] dark:text-white">Management Actions</h3>
            <span className="text-[11px] font-normal text-[#475569] dark:text-white/75">(Top Priorities)</span>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-left [&_td]:border-r [&_td]:border-[#f1f5f9] [&_td:last-child]:border-r-0 [&_th]:border-r [&_th]:border-[#f1f5f9] [&_th:last-child]:border-r-0 dark:[&_td]:border-white/[0.04] dark:[&_th]:border-white/[0.05]">
            <thead>
              <tr className="border-b border-t border-[#f1f5f9] bg-[#f8fafc] dark:border-white/[0.05] dark:bg-white/[0.02]">
                <th className="whitespace-nowrap px-5 py-3 text-center align-middle text-[13px] font-bold tracking-wide text-[#475569] dark:text-white/60">Priority</th>
                <th className="w-[26%] px-3 py-3 text-center align-middle text-[13px] font-bold tracking-wide text-[#475569] dark:text-white/60">Action</th>
                <th className="px-3 py-3 text-center align-middle text-[13px] font-bold tracking-wide text-[#475569] dark:text-white/60">SSP</th>
                <th className="px-3 py-3 text-center align-middle text-[13px] font-bold tracking-wide text-[#475569] dark:text-white/60">Application</th>
                <th className="whitespace-nowrap px-3 py-3 text-center align-middle text-[13px] font-bold tracking-wide text-[#475569] dark:text-white/60">Owner</th>
                <th className="whitespace-nowrap px-3 py-3 text-center align-middle text-[13px] font-bold tracking-wide text-[#475569] dark:text-white/60">Target Date</th>
                <th className="whitespace-nowrap px-3 py-3 pr-5 text-center align-middle text-[13px] font-bold tracking-wide text-[#475569] dark:text-white/60">Progress</th>
              </tr>
            </thead>
            <tbody>
              {visibleActions.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-5 py-12 text-center align-middle">
                    <span className="text-sm font-bold text-[#1e3a8a] dark:text-blue-300">No Data Available</span>
                  </td>
                </tr>
              ) : (
                visibleActions.map((a, i) => (
                <tr key={i} className="border-b border-[#f1f5f9] transition-colors hover:bg-[#f8fafc] dark:border-white/[0.04] dark:hover:bg-white/[0.02]">
                  <td className="whitespace-nowrap px-5 py-3.5 text-center align-middle">
                    <span className={`inline-flex min-w-[84px] items-center justify-center rounded-full px-2.5 py-0.5 text-[10px] font-black capitalize tracking-wide ${a.badge}`}>{a.priority.toLowerCase()}</span>
                  </td>
                  <td className="w-[26%] px-3 py-3.5 align-middle">
                    <p className="break-words text-[13.5px] font-medium leading-snug text-[#1e293b] dark:text-white/90">{a.label}</p>
                  </td>
                  <td className="px-3 py-3.5 align-middle">
                    <span className="break-words text-[13px] text-[#334155] dark:text-white/70">{a.sspName || "-"}</span>
                  </td>
                  <td className="px-3 py-3.5 align-middle">
                    <span className="break-words text-[13px] text-[#334155] dark:text-white/70">{a.applicationName || "-"}</span>
                  </td>
                  <td className="whitespace-nowrap px-3 py-3.5 align-middle">
                    <div className="flex items-center justify-start gap-2">
                      <div className="flex size-6 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-indigo-100 to-violet-100 text-[8px] font-black text-indigo-700 dark:from-indigo-500/20 dark:to-violet-500/20 dark:text-indigo-300">
                        {a.initials}
                      </div>
                      <span className="text-[13px] text-[#334155] dark:text-white/70">{a.owner}</span>
                    </div>
                  </td>
                  <td className="whitespace-nowrap px-3 py-3.5 text-center align-middle">
                    <span className="break-normal text-[13px] text-[#475569] dark:text-white/60">{a.date}</span>
                  </td>
                  <td className="whitespace-nowrap px-3 py-3.5 pr-5 align-middle">
                    <div className="flex min-w-[110px] items-center gap-2">
                      <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-[#f1f5f9] dark:bg-white/[0.07]">
                        <div className="h-full rounded-full" style={{ width: `${a.progress}%`, background: `linear-gradient(90deg, ${a.color}70, ${a.color})` }} />
                      </div>
                      <span className="w-9 shrink-0 text-right text-[12px] font-bold text-[#334155] dark:text-white/60">{a.progress}%</span>
                    </div>
                  </td>
                </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {hasMoreActions && (
          <div className="border-t border-[#f1f5f9] px-5 py-3.5 dark:border-white/[0.04]">
            <button
              onClick={() => setShowAllActions(true)}
              className="flex items-center gap-1 text-[13px] font-semibold text-indigo-500 hover:underline dark:text-indigo-400"
            >
              View all actions ({actions.length}) <ArrowRight className="size-3.5" />
            </button>
          </div>
        )}
      </div>

      {/* ── Key Metrics — grouped by API section (heading + cards per field) ── */}
      <div className="space-y-6 pt-3">
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <h3 className="text-base font-bold text-[#0f172a] dark:text-white">Key Metrics Overview</h3>
            <p className="mt-0.5 text-[13px] text-[#64748b] dark:text-white/55">Important metrics at a glance.</p>
          </div>
          <button
            onClick={() => setConfigOpen(true)}
            className="inline-flex shrink-0 items-center gap-1.5 rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-indigo-700"
          >
            <Settings2 className="size-3.5" /> Configure
          </button>
        </div>

        {insightsLoading ? (
          <div className={`grid gap-3 ${KM_GRID_COLS[kmConfig.columns] ?? KM_GRID_COLS[4]}`}>
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="h-[104px] animate-pulse rounded-xl border border-[#e2e8f0] bg-[#fafbfc] dark:border-white/[0.06] dark:bg-white/[0.02]" />
            ))}
          </div>
        ) : countSections.length === 0 ? (
          <p className="rounded-xl border border-dashed border-[#e2e8f0] py-10 text-center text-sm text-[#64748b] dark:border-white/[0.08] dark:text-white/50">
            No metrics available.
          </p>
        ) : (
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 2xl:grid-cols-3">
          {countSections.map((section) => {
            const tone = COUNT_TONES[GROUP_TONE[section.group] ?? "brandIndigo"] ?? COUNT_TONES.brandIndigo;
            const GroupIcon = GROUP_ICON[section.group] ?? Activity;
            return (
            <div key={section.title} className="group/panel overflow-hidden rounded-2xl border border-[#e2e8f0] bg-white shadow-sm transition-shadow duration-200 hover:shadow-md dark:border-white/[0.06] dark:bg-[#131320]">
              {/* group header — icon + colored title + subtitle + metric count */}
              <div className="flex items-center gap-3 border-b border-[#f1f5f9] bg-[#fafbfc] px-5 py-4 dark:border-white/[0.05] dark:bg-white/[0.015]">
                <div className={`flex size-9 shrink-0 items-center justify-center rounded-lg ${tone.iconBox}`}>
                  <GroupIcon className="size-5" />
                </div>
                <div className="min-w-0 flex-1">
                  <h4 className={`text-sm font-bold ${tone.value}`}>{section.title}</h4>
                  {section.desc && (
                    <p className="mt-0.5 break-words text-xs leading-snug text-[#64748b] dark:text-white/55">{section.desc}</p>
                  )}
                </div>
                <span className="shrink-0 rounded-full bg-white px-2 py-0.5 text-[11px] font-bold text-[#64748b] shadow-sm ring-1 ring-[#e2e8f0] dark:bg-white/[0.06] dark:text-white/60 dark:ring-white/10">
                  {section.cards.length}
                </span>
              </div>
              {/* stat cards — each a self-contained tile with a group-tone accent
                  spine, a field icon chip, the colored value, label + sub, and an
                  optional source chip. Auto-fit so tiles fill and wrap per panel. */}
              <div className="grid gap-3 p-4 [grid-template-columns:repeat(auto-fit,minmax(150px,1fr))]">
                {section.cards.map((f) => (
                  <div
                    key={f.key}
                    className={`group/stat flex min-w-0 flex-col rounded-xl border border-l-[3px] border-[#eef2f6] ${f.accent} bg-[#fafbfc] px-4 py-3.5 transition-all duration-200 hover:-translate-y-0.5 hover:border-[#e2e8f0] hover:shadow-[0_10px_24px_-14px_rgba(2,6,23,0.28)] dark:border-white/[0.06] dark:bg-white/[0.02] dark:hover:bg-white/[0.035]`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      {f.tooltip ? (
                        <Tip label={f.tooltip} side="top" contentClassName="max-w-[260px] leading-snug font-normal">
                          <p className={`inline-block break-words text-xl font-bold leading-none tabular-nums ${f.valueColor}`}>
                            {f.display}
                          </p>
                        </Tip>
                      ) : (
                        <p className={`break-words text-xl font-bold leading-none tabular-nums ${f.valueColor}`}>
                          {f.display}
                        </p>
                      )}
                      <div className={`flex size-8 shrink-0 items-center justify-center rounded-lg ${f.iconBox} opacity-90 transition-opacity group-hover/stat:opacity-100`}>
                        <f.Icon className="size-4" />
                      </div>
                    </div>
                    <p className="mt-3 break-words text-xs font-bold leading-tight text-[#0f172a] dark:text-white/90">{f.label}</p>
                    <p className="mt-0.5 break-words text-xs leading-snug text-[#64748b] dark:text-white/55">{f.sub}</p>
                    {FIELD_SOURCE[f.key] && (
                      <span className="mt-2.5 inline-flex w-fit max-w-full items-center break-words rounded-md border border-indigo-200 bg-indigo-50 px-2 py-0.5 text-[10px] font-semibold text-indigo-600 dark:border-indigo-400/30 dark:bg-indigo-500/10 dark:text-indigo-300">
                        {FIELD_SOURCE[f.key]}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
            );
          })}
          </div>
        )}
      </div>

      {/* Config modal — drag cards between panels + pick cards/row (persisted) */}
      {configOpen && (
        <KeyMetricsConfigModal
          groups={allGroups}
          config={kmConfig}
          onApply={applyKmConfig}
          onClose={() => setConfigOpen(false)}
        />
      )}
        </>
      )}
      </div>
      {/* end zoomed content wrapper */}

      {/* ── Detail Drawer (drill-down) ────────────────────────────────────── */}
      {drawer && config && (
        <>
          <div className="fixed inset-0 z-40 bg-black/40 backdrop-blur-md" onClick={closeDrawer} />

          <div className="fixed right-0 top-0 z-50 flex h-full w-full max-w-[760px] flex-col border-l border-[#e2e8f0] bg-white shadow-2xl dark:border-white/[0.08] dark:bg-[#0f0e1f]">
            {/* Header */}
            <div className={`${config.headerBg} flex-shrink-0 border-b ${config.headerBorder} px-6 pb-5 pt-6`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex min-w-0 items-start gap-3.5">
                  <div className={`flex size-11 shrink-0 items-center justify-center rounded-xl ${config.iconBg}`}>
                    <config.icon className={`size-5 ${config.iconColor}`} />
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <h2 className="break-words text-base font-bold leading-tight text-[#0f172a] dark:text-white">
                        {drawerSingle ? drawerSingle.title : config.title}
                      </h2>
                      <span className="shrink-0 rounded-full bg-[#f1f5f9] px-2 py-0.5 text-[11px] font-bold text-[#0f172a] dark:bg-white/10 dark:text-white">
                        {drawerDetailCount}
                      </span>
                    </div>
                    <p className="mt-1.5 break-words text-[13px] leading-relaxed text-[#475569] dark:text-white/75">
                      {drawerSingle ? drawerSingle.subtitle : config.subtitle}
                    </p>
                  </div>
                </div>
                <button
                  onClick={closeDrawer}
                  className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-[#f1f5f9] text-[#64748b] transition-colors hover:bg-[#e2e8f0] dark:bg-white/[0.06] dark:text-white/40 dark:hover:bg-white/[0.12]"
                >
                  <X className="size-4" />
                </button>
              </div>
            </div>

            {/* Details list (grouped by item) */}
            <div className="flex-1 overflow-y-auto">
              {drawerDetailCount === 0 ? (
                <div className="px-6 py-12 text-center">
                  <p className="text-sm text-[#64748b] dark:text-white/50">No detail records in this category.</p>
                </div>
              ) : (
                drawer.items.map((item, gi) => {
                  const isMulti = drawer.items.length > 1;
                  const expanded = !isMulti || expandedGroups.has(gi);
                  return (
                    <div key={gi} className="px-6">
                      {isMulti && (
                        <button
                          onClick={() => toggleGroup(gi)}
                          className="sticky top-0 z-10 flex w-full items-center justify-between gap-2 border-b border-[#f1f5f9] bg-white/95 py-3.5 text-left backdrop-blur-sm dark:border-white/[0.05] dark:bg-[#0f0e1f]/95"
                        >
                          <div className="flex min-w-0 items-center gap-2">
                            <ChevronDown className={`size-4 shrink-0 text-[#94a3b8] transition-transform dark:text-white/40 ${expanded ? "" : "-rotate-90"}`} />
                            <p className="break-words text-xs font-semibold tracking-wider text-[#0f172a] dark:text-white/70">{item.title}</p>
                          </div>
                          <span className="shrink-0 rounded-full bg-[#f1f5f9] px-2 py-0.5 text-[11px] font-bold text-[#0f172a] dark:bg-white/10 dark:text-white">
                            {item.details.length}
                          </span>
                        </button>
                      )}

                      {expanded && (
                        <>
                          {item.recommendedAction && (
                            <div className="mt-5 space-y-4 rounded-2xl border border-[#eef2f6] bg-[#f8fafc] p-4 dark:border-white/[0.06] dark:bg-white/[0.02]">
                              <div>
                                <p className="text-[13px] font-semibold text-[#64748b] dark:text-white/60">Recommended Action</p>
                                <p className="mt-1.5 break-words text-sm leading-relaxed text-[#334155] dark:text-white/90">{item.recommendedAction}</p>
                              </div>
                            </div>
                          )}

                          {drawer.section.type === "improved" ? (
                            /* "What Improved" — show the findings in a table. */
                            <div className="pb-2 pt-3">
                              <DataTable<InsightDetail>
                                columns={IMPROVED_COLUMNS}
                                data={item.details}
                                rowKey={(d) => d.id}
                                compact
                                emptyText="No improvements in this group."
                              />
                            </div>
                          ) : drawer.section.type === "worsePoams" ? (
                            /* "Top POA&M Risks" — findings with severity + status. */
                            <div className="pb-2 pt-3">
                              <DataTable<InsightDetail>
                                columns={WORSEPOAMS_COLUMNS}
                                data={item.details}
                                rowKey={(d) => d.id}
                                compact
                                maxHeight="60vh"
                                emptyText="No POA&M items in this group."
                              />
                            </div>
                          ) : (
                            /* Spaced record cards (was a tight divider list) */
                            <div className="space-y-2.5 pb-2 pt-3">
                              {item.details.map((d) => (
                                <div
                                  key={d.id}
                                  className="flex items-center justify-between gap-3 rounded-xl border border-[#eef2f6] bg-[#fafbfc] px-4 py-3.5 transition-colors hover:border-[#e2e8f0] dark:border-white/[0.05] dark:bg-white/[0.02] dark:hover:border-white/[0.1]"
                                >
                                  <div className="flex min-w-0 items-center gap-3">
                                    <div className={`size-2 shrink-0 rounded-full ${config.dotColor}`} />
                                    <div className="min-w-0">
                                      <p title={d.label} className="line-clamp-2 break-all text-[13px] font-semibold leading-snug text-[#1e293b] dark:text-white/90">{d.label}</p>
                                      {d.sspName && <SspTag name={d.sspName} className="flex" />}
                                    </div>
                                  </div>
                                  {(d.previousMetric || !isHiddenMetric(d.metric)) && (
                                    <div className="flex shrink-0 items-center gap-2">
                                      {d.previousMetric && (
                                        <>
                                          <span className="break-normal text-[13px] font-medium text-[#1e293b] dark:text-white/90">{formatMetricDates(d.previousMetric)}</span>
                                          <ArrowRight className="size-3.5 text-[#94a3b8] dark:text-white/35" />
                                        </>
                                      )}
                                      {!isHiddenMetric(d.metric) && (
                                        <span className="break-normal text-[13px] font-medium tabular-nums text-[#1e293b] dark:text-white/90">
                                          {formatMetricDates(d.metric)}
                                        </span>
                                      )}
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  );
                })
              )}
            </div>

            {/* Footer */}
            <div className="flex-shrink-0 border-t border-[#f1f5f9] bg-[#f8fafc] px-6 py-4 dark:border-white/[0.06] dark:bg-white/[0.02]">
              <p className="text-[11px] text-[#94a3b8] dark:text-white/30">
                Showing {drawerDetailCount} {drawerDetailCount === 1 ? "record" : "records"}
              </p>
            </div>
          </div>
        </>
      )}

      {/* ── All Management Actions (slide-in panel) ───────────────────────── */}
      {showAllActions && (
        <>
          <div className="fixed inset-0 z-40 bg-black/40 backdrop-blur-md" onClick={() => setShowAllActions(false)} />

          <div className="fixed right-0 top-0 z-50 flex h-full w-full max-w-[760px] flex-col border-l border-[#e2e8f0] bg-white shadow-2xl dark:border-white/[0.08] dark:bg-[#0f0e1f]">
            {/* Header */}
            <div className="flex-shrink-0 border-b border-violet-100 bg-violet-50/60 px-6 pb-5 pt-6 dark:border-violet-500/15 dark:bg-violet-500/[0.06]">
              <div className="flex items-start justify-between gap-3">
                <div className="flex min-w-0 items-start gap-3.5">
                  <div className="flex size-11 shrink-0 items-center justify-center rounded-xl bg-violet-100 dark:bg-violet-500/15">
                    <CheckCircle2 className="size-5 text-violet-600 dark:text-violet-400" />
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <h2 className="break-words text-base font-bold leading-tight text-[#0f172a] dark:text-white">
                        Management Actions
                      </h2>
                      <span className="shrink-0 rounded-full bg-violet-100 px-2 py-0.5 text-[11px] font-black text-violet-600 dark:bg-violet-500/15 dark:text-violet-400">
                        {actions.length}
                      </span>
                    </div>
                    <p className="mt-1.5 break-words text-[13px] leading-relaxed text-[#475569] dark:text-white/75">
                      Top remediation priorities across all applications
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setShowAllActions(false)}
                  className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-[#f1f5f9] text-[#64748b] transition-colors hover:bg-[#e2e8f0] dark:bg-white/[0.06] dark:text-white/40 dark:hover:bg-white/[0.12]"
                >
                  <X className="size-4" />
                </button>
              </div>
            </div>

            {/* Action cards */}
            <div className="flex-1 space-y-2.5 overflow-y-auto px-6 py-4">
              {actions.map((a, i) => (
                <div
                  key={i}
                  className="rounded-xl border border-[#eef2f6] bg-[#fafbfc] px-4 py-3.5 transition-colors hover:border-[#e2e8f0] dark:border-white/[0.05] dark:bg-white/[0.02] dark:hover:border-white/[0.1]"
                >
                  <div className="flex items-start justify-between gap-3">
                    <span className={`inline-flex min-w-[78px] shrink-0 items-center justify-center rounded-full px-2.5 py-0.5 text-[9px] font-black capitalize tracking-wide ${a.badge}`}>{a.priority.toLowerCase()}</span>
                    <span className="break-normal text-[11px] text-[#475569] dark:text-white/60">{a.date}</span>
                  </div>
                  <p title={a.label} className="mt-2 line-clamp-2 break-words text-[12px] font-semibold leading-snug text-[#1e293b] dark:text-white/90">{a.label}</p>
                  {a.sspName && <SspTag name={a.sspName} className="mt-1 flex" />}
                  <div className="mt-3 flex items-center justify-between gap-3">
                    <div className="flex min-w-0 items-center gap-2">
                      <div className="flex size-5 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-indigo-100 to-violet-100 text-[7px] font-black text-indigo-700 dark:from-indigo-500/20 dark:to-violet-500/20 dark:text-indigo-300">
                        {a.initials}
                      </div>
                      <span className="truncate text-[11px] text-[#334155] dark:text-white/70">{a.owner}</span>
                    </div>
                    <div className="flex min-w-[110px] shrink-0 items-center gap-2">
                      <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-[#f1f5f9] dark:bg-white/[0.07]">
                        <div className="h-full rounded-full" style={{ width: `${a.progress}%`, background: `linear-gradient(90deg, ${a.color}70, ${a.color})` }} />
                      </div>
                      <span className="w-9 shrink-0 text-right text-[11px] font-bold text-[#334155] dark:text-white/60">{a.progress}%</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Footer */}
            <div className="flex-shrink-0 border-t border-[#f1f5f9] bg-[#f8fafc] px-6 py-4 dark:border-white/[0.06] dark:bg-white/[0.02]">
              <p className="text-[11px] text-[#94a3b8] dark:text-white/30">
                Showing {actions.length} {actions.length === 1 ? "action" : "actions"}
              </p>
            </div>
          </div>
        </>
      )}
    </div>
  );
}